import React, { useMemo, useCallback } from 'react';
import moment from 'moment';
import {
  Box,
  Button,
  Icon,
  icons,
  Menu,
  MenuButton,
  MenuList,
  MenuItemOption,
  MenuOptionGroup,
  Text,
} from '@tapresearch/ui/components';

import { Campaign } from '../../../../api';
import { WavesViewState, WavesViewOption, WAVES_VIEW_OPTIONS } from './';

const WAVES_VIEW_LABELS = {
  all: 'All waves',
  last4: 'Last 4 waves',
  last12: 'Last 12 waves',
};

interface WaveTimelineSelectProps {
  waves: Campaign[];
  wavesView: WavesViewState;
  onChangeWavesView: (view: WavesViewOption) => void;
}
export const WaveTimelineSelect = ({
  waves,
  wavesView,
  onChangeWavesView,
}: WaveTimelineSelectProps) => {
  const numWaves = waves.length;
  const getWaves = useCallback(() => {
    switch (wavesView.option) {
      case WAVES_VIEW_OPTIONS.all: {
        return [waves[0], waves[numWaves - 1]];
      }
      case WAVES_VIEW_OPTIONS.last4: {
        return [waves[0], numWaves >= 4 ? waves[3] : waves[numWaves - 1]];
      }
      case WAVES_VIEW_OPTIONS.last12: {
        return [waves[0], numWaves >= 12 ? waves[11] : waves[numWaves - 1]];
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(waves), wavesView, numWaves]);

  const waveDateDisplayValue = useMemo(() => {
    const [lastWave, firstWave] = getWaves();
    return {
      short: `${moment(firstWave.attributes.launch_date).format(
        'MMM Do, YYYY'
      )} - ${moment(lastWave.attributes.launch_date).format('MMM Do, YYYY')}`,
      long: `${moment(firstWave.attributes.launch_date).format(
        'MMMM Do, YYYY'
      )} - ${moment(lastWave.attributes.launch_date).format('MMMM Do, YYYY')}`,
    };
  }, [getWaves]);

  return (
    <Menu isLazy autoSelect={false}>
      <MenuButton
        as={Button}
        data-testid="wave-timeline-select-trigger"
        variant="ghost"
        fontWeight="medium"
        colorScheme="gray"
        color="gray.600"
        rightIcon={<Icon icon={icons.fi.ChevronDown} />}
      >
        {waveDateDisplayValue.long}
      </MenuButton>
      <MenuList
        data-testid="wave-timeline-select-menu"
        sx={{
          '.chakra-menu__icon-wrapper': {
            d: 'none',
          },
        }}
      >
        <Box
          py={2}
          px={4}
          color="gray.700"
          fontWeight="medium"
          borderBottom="1px solid"
          borderBottomColor="gray.200"
        >
          <Text as={Box} fontSize="xs">
            {WAVES_VIEW_LABELS[wavesView.option]}
          </Text>
          <Text as={Box} py={2} fontSize="sm">
            {waveDateDisplayValue.short}
          </Text>
        </Box>
        <Box p={3}>
          <MenuOptionGroup
            onChange={onChangeWavesView}
            defaultValue={wavesView.option}
            type="radio"
          >
            <MenuItemOption
              {...Styles.MenuItem}
              {...(wavesView.option === WAVES_VIEW_OPTIONS.all && {
                ...Styles.MenuItemSelected,
              })}
              value={WAVES_VIEW_OPTIONS.all}
            >
              All waves
            </MenuItemOption>
            <MenuItemOption
              {...Styles.MenuItem}
              {...(wavesView.option === WAVES_VIEW_OPTIONS.last4 && {
                ...Styles.MenuItemSelected,
              })}
              {...(numWaves < 5 && {
                isDisabled: true,
              })}
              value={WAVES_VIEW_OPTIONS.last4}
            >
              Last 4 waves
            </MenuItemOption>
            <MenuItemOption
              {...Styles.MenuItem}
              {...(wavesView.option === WAVES_VIEW_OPTIONS.last12 && {
                ...Styles.MenuItemSelected,
              })}
              {...(numWaves < 13 && {
                isDisabled: true,
              })}
              value={WAVES_VIEW_OPTIONS.last12}
            >
              Last 12 waves
            </MenuItemOption>
          </MenuOptionGroup>
        </Box>
      </MenuList>
    </Menu>
  );
};

const Styles = {
  MenuItem: {
    rounded: 'md',
    fontSize: 'md',
    fontWeight: 'medium',
    color: 'gray.500',
    mb: 1,
    _focus: {
      bg: 'blue.50',
    },
    _hover: {
      bg: 'blue.50',
    },
  },
  MenuItemSelected: {
    bg: 'blue.50',
    color: 'blue.500',
  },
};
